import { Box, Chip } from '@mui/material'
import ForkRightIcon from '@mui/icons-material/ForkRight'
import SectionTitle from '../SectionTitle'
import SettingsRow from '../SettingsRow'
import SettingsSwitchRow from '../SettingsSwitchRow'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { usePattern } from '../../../hooks/usePattern'

function TradeExitSettings({ backtest }: any) {
  const { usermeta, canShowcaseViewBacktestSettings } = useApplicationSuiteContext()

  const ownsBacktest = backtest.gcid === usermeta.gcid

  const [exitPattern] = usePattern(
    backtest?.backtestSettings?.exitPatternId,
    backtest?.backtestSettings?.exitPatternVersion,
    backtest?.backtestSettings?.gcid || backtest?.gcid
  )

  const exitStrategyRaw = backtest.backtestSettings.generateTrades.exitStrategy
  const exitStrategy =
    exitStrategyRaw === 'peak_valley'
      ? 'Fractal'
      : exitStrategyRaw === 'high_low'
        ? 'High/Low'
        : exitStrategyRaw[0].toUpperCase() + exitStrategyRaw.slice(1)

  const riskReward = (
    <Box display="inline-flex" alignItems="center" gap={0.5} flexWrap="wrap">
      {backtest.backtestSettings.generateTrades.tradeRatios.map((ratio: any, idx: number) => (
        <Chip
          key={idx}
          size="small"
          variant="outlined"
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>
                {ratio.risk}/{ratio.reward}
              </span>
              {ratio.conditions?.length > 0 && (
                <>
                  <span>·</span>
                  <Box display="flex" alignItems="center">
                    <span>{ratio.conditions.length}</span>
                    <ForkRightIcon sx={{ fontSize: 14 }} />
                  </Box>
                </>
              )}
            </Box>
          }
        />
      ))}
    </Box>
  )
  const atrPeriod = backtest.backtestSettings.generateTrades.atrPeriod
  const atrChartPeriod = backtest.backtestSettings.generateTrades.atrChartPeriod
  const atrChartTimeframe = backtest.backtestSettings.generateTrades.atrChartTimeframe

  const maxLookback = backtest.backtestSettings.generateTrades.maxLookback
  const fractalCount = backtest.backtestSettings.generateTrades.fractalCount
  const maxPeakValleyLookback = backtest.backtestSettings.generateTrades.maxPeakValleyLookback
  const priceOffset = backtest.backtestSettings.generateTrades.priceOffset

  const exitPatternLabel = exitPattern
    ? `${exitPattern.name} v${exitPattern.version}`
    : backtest.exitPatternName
      ? `${backtest.exitPatternName} v${backtest.backtestSettings.exitPatternVersion}`
      : null

  const exitPatternModeRaw = backtest.backtestSettings.exitPatternMode || 'both'
  const exitPatternModeLabel =
    exitPatternModeRaw === 'take_profit_only'
      ? 'Take Profit Only'
      : exitPatternModeRaw === 'stop_loss_only'
        ? 'Stop Loss Only'
        : 'Both'

  const exitTrigger = exitPattern?.trigger
    ? `${exitPattern.trigger.chartDetails.periodicity} ${exitPattern.trigger.chartDetails.timeframe} at post`
    : null

  return (
    <Box>
      <SectionTitle title="Trade Exit Settings" />
      <SettingsRow
        title="Stop Loss Strategy"
        data={
          canShowcaseViewBacktestSettings || ownsBacktest
            ? exitStrategy
            : 'Backtest settings not available'
        }
      />
      <SettingsRow
        title="Risk / Reward"
        data={
          canShowcaseViewBacktestSettings || ownsBacktest
            ? riskReward
            : 'Backtest settings not available'
        }
      />
      {exitPatternLabel && (
        <SettingsRow
          title="Trade Exit Pattern"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? exitPatternLabel
              : 'Backtest settings not available'
          }
        />
      )}
      {exitPatternLabel && (
        <SettingsRow
          title="Exit Pattern Mode"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? exitPatternModeLabel
              : 'Backtest settings not available'
          }
        />
      )}
      {exitTrigger && (
        <SettingsRow
          title="Exit Trigger"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? exitTrigger
              : 'Backtest settings not available'
          }
        />
      )}

      {exitStrategyRaw === 'high_low' && (
        <SettingsRow
          title="Candle Lookback"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${maxLookback}`
              : 'Backtest settings not available'
          }
        />
      )}
      {exitStrategyRaw === 'high_low' && (
        <SettingsRow
          title="Price Offset"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${priceOffset}`
              : 'Backtest settings not available'
          }
        />
      )}

      {exitStrategyRaw === 'peak_valley' && (
        <SettingsRow
          title="Fractal Count"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${fractalCount}`
              : 'Backtest settings not available'
          }
        />
      )}
      {exitStrategyRaw === 'peak_valley' && (
        <SettingsRow
          title="Max Peak/Valley Lookback"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${maxPeakValleyLookback}`
              : 'Backtest settings not available'
          }
        />
      )}
      {exitStrategyRaw === 'peak_valley' && (
        <SettingsRow
          title="Price Offset"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${priceOffset}`
              : 'Backtest settings not available'
          }
        />
      )}

      {(exitStrategyRaw === 'atr' || exitStrategyRaw === 'peak_valley') && (
        <SettingsRow
          title="ATR Period"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${atrPeriod}`
              : 'Backtest settings not available'
          }
        />
      )}
      {(exitStrategyRaw === 'atr' || exitStrategyRaw === 'peak_valley') && (
        <SettingsRow
          title="ATR Chart"
          data={
            canShowcaseViewBacktestSettings || ownsBacktest
              ? `${atrChartPeriod} ${atrChartTimeframe}`
              : 'Backtest settings not available'
          }
        />
      )}

      {backtest.backtestSettings.analyzeTrades.dynamicTakeProfit &&
        (canShowcaseViewBacktestSettings || ownsBacktest) && (
          <SettingsSwitchRow
            title={'Periodically modify take profit'}
            data={!!backtest.backtestSettings.analyzeTrades.dynamicTakeProfit}
          />
        )}
      {backtest.backtestSettings.analyzeTrades.forceExit?.time &&
        (canShowcaseViewBacktestSettings || ownsBacktest) && (
          <SettingsSwitchRow
            title={'Force exit on time elapsed'}
            data={!!backtest.backtestSettings.analyzeTrades.forceExit?.time}
          />
        )}
    </Box>
  )
}

export default TradeExitSettings
